import React, { ComponentProps, CSSProperties, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import cn from 'classnames'
import styles from './style.module.css'

type ClassNames = Exclude<ComponentProps<typeof CSSTransition>['classNames'], string>

export interface Props {
  isOpen: boolean
  unmountOnExit?: boolean
  classNames?: ClassNames
  timeout?:
    | number
    | {
        enter: number
        exit: number
      }
  children: React.ReactNode
  onAnimationEnd?: (isOpen: boolean) => void
}

/**
 * Collapse animation block
 */
function Collapse(props: Props) {
  const { children, isOpen = false, timeout = 200, unmountOnExit = true } = props

  const wrapperRef = useRef<HTMLDivElement>(null)

  const [height, setHeight] = useState(() => (props.isOpen ? '' : '0'))

  const onAnimationEnd = (isOpen: boolean) => {
    props.onAnimationEnd?.(isOpen)
  }

  const show = () => {
    const element = wrapperRef.current?.children[0]
    if (!element) {
      return
    }

    const wrapperHeight = (element as HTMLElement).offsetHeight ?? 0
    setHeight(`${wrapperHeight}px`)
  }

  const hide = () => {
    setHeight('0')
  }

  const reset = () => {
    setHeight('')
    onAnimationEnd(true)
  }

  const onExiting = () => setTimeout(hide, 0)

  const timeoutEnter = typeof timeout === 'object' ? timeout.enter : timeout
  const timeoutExit = typeof timeout === 'object' ? timeout.exit : timeout

  const classNames: ClassNames = {
    ...(props.classNames || {}),
    enter: cn(styles.enter, props.classNames?.enter),
    exit: cn(styles.exit, props.classNames?.exit),
  }

  const style: CSSProperties = {
    ['--duration-enter' as string]: `${timeoutEnter}ms`,
    ['--duration-exit' as string]: `${timeoutExit}ms`,
  }

  if (height) {
    style.height = height
  }

  return (
    <CSSTransition
      in={isOpen}
      timeout={timeout}
      classNames={classNames}
      onEnter={hide}
      onEntering={show}
      onEntered={reset}
      onExit={show}
      onExiting={onExiting}
      unmountOnExit={unmountOnExit}
      onExited={() => onAnimationEnd(false)}
    >
      <div className={styles.container} style={style} ref={wrapperRef}>
        {children}
      </div>
    </CSSTransition>
  )
}

export default Collapse
